import { IOrderItem, IDiscountItemMap } from '../../model/order.store'
import { includes, sumBy, forEach, orderBy, get } from 'lodash'
import { isAfter, isBefore } from 'date-fns'
import { getItemSoldPrice } from '../order.util'

/**
 * Chương trình sale Set quà 8/3:
 * Khi mua 1 bánh bất kì (không tính các SKU sale) sẽ được mua kèm 1 set giá 99k
 * Ví dụ: 3 bánh -> được giảm 3 set bất kể SKU trong list sale
 */

// Danh sách SKU được giảm giá còn 99k
const SET_QUA_83_SKUS = [
  // 'V10176', // Set quà bông lan trứng muối + trà đào
  // 'V9446', // Set quà bánh kem tulip + trà vải hoa hồng
  'V8993', // Hộp 12 bánh su kem Singapore trang trí hoa quả tươi
  'V9115'
]

const SALE_PRICE = 99000

interface IProcessSetQua83Params {
  items: IOrderItem[]
  resultMap: IDiscountItemMap
  orderPlatform: string
  orderType: string
}

export function processSetQua83Sale({
  items,
  resultMap,
  orderPlatform,
  orderType
}: IProcessSetQua83Params): void {
  // Chỉ áp dụng cho đơn trực tiếp (SAVOR_SHOP)
  if (orderPlatform !== 'SAVOR_SHOP') {
    return
  }

  if (isAfter(new Date(), new Date('2026-03-18T00:00:00+07:00'))) {
    return
  }

  // Đếm số lượng bánh (không tính các SKU sale)
  const qualifyingCakeCount = sumBy(items, item => {
    if (includes(SET_QUA_83_SKUS, item.sku)) {
      return 0
    }
    return item.quantity
  })

  if (qualifyingCakeCount <= 0) {
    return
  }

  // Số set được giảm giá = số bánh mua
  let remainingDiscountSets = qualifyingCakeCount

  items.forEach(item => {
    if (!includes(SET_QUA_83_SKUS, item.sku) || remainingDiscountSets <= 0) {
      return
    }

    const promotionalQuantity = Math.min(item.quantity, remainingDiscountSets)

    if (promotionalQuantity > 0) {
      const soldPrice = getItemSoldPrice({
        item,
        orderPlatform,
        orderType
      })
      const discountPerSet = soldPrice - SALE_PRICE

      if (discountPerSet > 0) {
        const discountAmount = discountPerSet * promotionalQuantity
        resultMap[item.id].discount += discountAmount
        resultMap[item.id].saleProgram = 'Sale 8/3: Mua bánh kèm set quà 99k'
        remainingDiscountSets -= promotionalQuantity
      }
    }
  })
}

/**
 * Chương trình mua 3 tính tiền 2 cho V8993 và V10176
 * Tính tổng cả 2 mã để xét, free cái rẻ nhất
 */
const BUY3PAY2_SKUS = [
  'V8993', // Hộp 12 bánh su kem Singapore trang trí hoa quả tươi
  // 'V10176' // Set quà bông lan trứng muối + trà đào
  'V9115'
]

export function processBuy3Pay2Sale({
  items,
  resultMap,
  orderPlatform,
  orderType
}: IProcessSetQua83Params): void {
  if (orderPlatform !== 'SAVOR_SHOP') {
    return
  }

  if (isAfter(new Date(), new Date('2026-03-18T00:00:00+07:00'))) {
    return
  }

  // Expand từng item ra để sort theo giá và free cái rẻ nhất
  const expandedItems: { sku: string; soldPrice: number; itemId: string }[] = []
  forEach(items, item => {
    if (!includes(BUY3PAY2_SKUS, item.sku)) {
      return
    }
    const soldPrice = getItemSoldPrice({ item, orderPlatform, orderType })
    for (let i = 0; i < item.quantity; i++) {
      expandedItems.push({ sku: item.sku, soldPrice, itemId: item.id })
    }
  })

  const totalQuantity = expandedItems.length
  if (totalQuantity < 3) {
    return
  }

  // Số cái được free = mua 3 tính 2 -> floor(total / 3)
  const numFreeItems = Math.floor(totalQuantity / 3)

  // Sort giá tăng dần, free cái rẻ nhất trước
  const sortedItems = orderBy(expandedItems, ['soldPrice'], ['asc'])

  // Tính discount theo itemId
  const discountByItemId: { [itemId: string]: number } = {}
  let remainingFree = numFreeItems

  forEach(sortedItems, expandedItem => {
    if (remainingFree <= 0) {
      return
    }
    discountByItemId[expandedItem.itemId] =
      get(discountByItemId, expandedItem.itemId, 0) + expandedItem.soldPrice
    remainingFree--
  })

  // Áp dụng discount vào resultMap - chỉ ghi đè nếu giảm nhiều hơn chương trình trước
  forEach(items, item => {
    const buy3Pay2Discount = get(discountByItemId, item.id, 0)
    if (buy3Pay2Discount > 0 && buy3Pay2Discount > resultMap[item.id].discount) {
      resultMap[item.id].discount = buy3Pay2Discount
      resultMap[item.id].saleProgram = 'Sale 8/3: Mua 3 tính tiền 2'
    }
  })
}

/**
 * Chương trình đồng giá 99k cho V8993 và V9115
 * - Áp dụng từ 17/3 đến hết 21/3/2026
 */
const FLAT_99K_SKUS = ['V8993', 'V9115']
const FLAT_99K_PRICE = 99000
const FLAT_99K_START = new Date('2026-03-17T00:00:00+07:00')
const FLAT_99K_END = new Date('2026-03-22T00:00:00+07:00')

export function processFlat99kSale({
  items,
  resultMap,
  orderPlatform,
  orderType
}: IProcessSetQua83Params): void {
  if (orderPlatform !== 'SAVOR_SHOP') {
    return
  }

  const now = new Date()
  if (isBefore(now, FLAT_99K_START) || isAfter(now, FLAT_99K_END)) {
    return
  }

  forEach(items, item => {
    if (!includes(FLAT_99K_SKUS, item.sku)) {
      return
    }
    const soldPrice = getItemSoldPrice({ item, orderPlatform, orderType })
    const discountPerItem = soldPrice - FLAT_99K_PRICE
    if (discountPerItem > 0) {
      const discountAmount = discountPerItem * item.quantity
      resultMap[item.id].discount = discountAmount
      resultMap[item.id].saleProgram = 'Đồng giá 99k su kem'
    }
  })
}
